import { useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Avatar from "@/components/base/Avatar";
import { formatDate } from "@/utils/format";
import { usePager } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import type { Enrollment, SchoolClass, Student } from "@/types";
import { ENROLLMENT_STATUS_LABEL, ENROLLMENT_STATUS_TONE } from "@/utils/enrollment";

interface Props {
  open: boolean;
  onClose: () => void;
  students: Student[];
  enrollments: Enrollment[];
  classes: SchoolClass[];
  /** Année active : un élève déjà inscrit ou réinscrit cette année ne peut pas l'être à nouveau. */
  currentYear: string;
  onSelect: (student: Student) => void;
}

export default function ReenrollPicker({ open, onClose, students, enrollments, classes, currentYear, onSelect }: Props) {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const pager = usePager();

  const className = (id: string) => classes.find((c) => c.id === id)?.name ?? "—";

  const results = students.filter((s) => {
    const q = search.trim().toLowerCase();
    if (!q) return false;
    return s.lastName.toLowerCase().includes(q) || s.firstName.toLowerCase().includes(q) || s.matricule.toLowerCase().includes(q);
  });

  const page = pager.slice(results, search);

  const history = (studentId: string) =>
    enrollments.filter((e) => e.studentId === studentId).sort((a, b) => a.schoolYear.localeCompare(b.schoolYear));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Réinscrire un élève"
      subtitle="Recherchez un élève déjà inscrit. Son dossier et ses responsables sont conservés."
      icon="ri-user-follow-line"
      size="lg"
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2">
          <i className="ri-search-line text-foreground-400 text-sm"></i>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nom, prénom ou matricule..."
            className="w-full bg-transparent text-sm focus:outline-none"
          />
        </div>

        <div className="space-y-2">
          {page.items.map((s) => {
            const hist = history(s.id);
            const isOpen = expanded === s.id;
            const already = hist.some((e) => e.schoolYear === currentYear && e.status !== "CANCELLED");
            return (
              <div key={s.id} className="rounded-lg border border-background-200">
                <div className="flex items-center justify-between gap-3 p-3.5">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar lastName={s.lastName} firstName={s.firstName} src={s.photo || undefined} size="sm" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-900 truncate">
                        {s.firstName} {s.lastName}
                      </p>
                      <p className="text-xs text-foreground-500">{s.matricule}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => setExpanded(isOpen ? null : s.id)}
                      className="inline-flex items-center gap-1 rounded-md border border-background-300 px-2.5 py-1 text-xs text-foreground-700 hover:bg-background-100 cursor-pointer"
                    >
                      <i className="ri-history-line"></i>
                      Historique
                    </button>
                    {!already && (
                      <Button size="sm" icon="ri-refresh-line" onClick={() => onSelect(s)}>
                        Réinscrire
                      </Button>
                    )}
                  </div>
                </div>
                {already && (
                  <p role="status" className="border-t border-background-200 bg-secondary-50/70 px-3.5 py-2 text-xs text-secondary-800">
                    <i className="ri-information-line mr-1"></i>
                    Cet élève est déjà inscrit ou réinscrit cette année ({currentYear}).
                  </p>
                )}
                {isOpen && (
                  <div className="border-t border-background-200 bg-background-100/50 px-3.5 py-3">
                    {hist.length > 0 ? (
                      <div className="space-y-1.5">
                        {hist.map((e) => (
                          <div key={e.id} className="flex items-center gap-2 text-xs text-foreground-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary-600"></span>
                            <span className="font-medium text-foreground-800">{e.schoolYear}</span>
                            <span>→</span>
                            <span>{className(e.classId)}</span>
                            <Badge tone={ENROLLMENT_STATUS_TONE[e.status]}>{ENROLLMENT_STATUS_LABEL[e.status]}</Badge>
                            <span className="text-foreground-400">{formatDate(e.enrollmentDate)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-foreground-400">Aucune inscription précédente.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <Pagination pager={page} className="!px-0" />
          {search.trim() && results.length === 0 && (
            <p className="rounded-md border border-dashed border-background-300 py-8 text-center text-sm text-foreground-400">
              Aucun élève trouvé pour « {search} ».
            </p>
          )}
          {!search.trim() && (
            <p className="rounded-md border border-dashed border-background-300 py-8 text-center text-sm text-foreground-400">
              Saisissez un nom, un prénom ou un matricule pour commencer.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}